import { compareResults, matchLabel } from "./match";
import type { SearchCategory, SearchResult, SearchResultKind } from "./types";

/** Rows fetched per entity before ranking; the tail is dropped after scoring. */
export const ENTITY_FETCH_LIMIT = 100;
/** Rows shown per entity group. */
export const ENTITY_RESULT_LIMIT = 5;
/**
 * Entity search costs a round trip and an ILIKE scan per table, so it waits for
 * a second character. Nav and company search (client-side) still run at one.
 */
export const MIN_ENTITY_QUERY = 2;

export interface EntityDisplay {
  id: string;
  title: string;
  subtitle: string;
  href: string;
  iconKey: string;
}

export function categoryForKind(kind: SearchResultKind): SearchCategory {
  if (kind === "company" || kind === "page" || kind === "custom_dashboard") return "navigate";
  if (kind === "help") return "help";
  if (["user", "comment"].includes(kind)) return "people";
  if ([
    "campaign", "campaign_task", "campaign_goal", "report_schedule", "approval",
    "recommendation", "research", "task_brief", "audit_finding", "post_note",
  ].includes(kind)) return "work";
  if ([
    "report", "sitemap", "sitemap_page", "sitemap_section", "post", "social_copy",
    "media", "document", "pulse_snapshot",
  ].includes(kind)) return "content";
  return "data";
}

/**
 * Score already-fetched rows against the query and keep the best few.
 *
 * The database returns matches but cannot rank them: ILIKE has no notion of
 * "the term starts the title". Re-running the shared matcher over the fetched
 * window gives entity results the same ordering and highlight ranges as the
 * client-side sources, so one merged list stays coherent.
 *
 * `build` returning null drops a row (a shape we cannot render a link for).
 */
export function rankEntityRows<T>(
  kind: SearchResultKind,
  rows: readonly T[],
  query: string,
  build: (row: T) => EntityDisplay | null,
  limit = ENTITY_RESULT_LIMIT,
): SearchResult[] {
  const results: SearchResult[] = [];
  for (const row of rows) {
    const display = build(row);
    if (!display) continue;
    const hit = matchLabel(display.title, query);
    // The row came back from an ILIKE on some column, but not necessarily the
    // one shown as the title (a website matches on its domain, a user on their
    // email). Keep it with a floor score so it still ranks under a title hit.
    results.push({
      id: `${kind}:${display.id}`,
      kind,
      category: categoryForKind(kind),
      title: display.title,
      subtitle: display.subtitle,
      href: display.href,
      iconKey: display.iconKey,
      matches: hit?.matches ?? [],
      score: hit?.score ?? 1,
    });
  }
  return results.sort(compareResults).slice(0, limit);
}

/** Merge rows from several column queries, keeping first-seen order, deduped by id. */
export function mergeById<T extends { id: string }>(...batches: (readonly T[] | null | undefined)[]): T[] {
  const seen = new Set<string>();
  const merged: T[] = [];
  for (const batch of batches) {
    for (const row of batch ?? []) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      merged.push(row);
    }
  }
  return merged;
}
